import {ApiProvider, ch, PageRoute, PageRouteProvider} from "@reapptor-apps/reapptor-react-common";
import {IFooterLink, IWizardStep, IWizardSteps} from "@reapptor-apps/reapptor-react-components";
import UserContext from "../models/server/UserContext";
import User from "@/models/server/User";
import CruiseConstants from "@/helpers/CruiseConstants";
import PageDefinitions from "@/providers/PageDefinitions";
import Context from "@/pages/Context";
import WizardContext from "@/pages/Cruise/WizardContext";
import Comparator from "@/helpers/Comparator";
import CruiseArea from "@/models/server/CruiseArea";
import CruiseCountry from "@/models/server/CruiseCountry";
import {CrossAuthSyncUserTokenData} from "@/models/server/CrossAuthSyncUserTokenData";
import {CruiseGroupSize} from "@/models/Enums";
import ApplicationContext from "@/models/server/ApplicationContext";
import TransformProvider from "@/providers/TransformProvider";
import EnumProvider from "@/providers/EnumProvider";
import Localizer from "@/localization/Localizer";

export interface IWizardNextStep {
    nextRoute: PageRoute;

    firstStep: boolean;

    lastStep: boolean;
}

class CruiseController {
    private _key: string | null = null;
    private _context: Context | null = null;
    private _initialized: boolean = false;
    private _initializing: boolean = false;

    private get key(): string {
        if (this._key == null) {
            this._key = `${CruiseConstants.applicationName}.${ch.getSessionId()}.${CruiseConstants.name}`;
        }
        return this._key!;
    }

    private indexOfCurrentStep(route: PageRoute): number {
        const wizard: IWizardSteps | null = this.getSteps();
        return (wizard)
            ? wizard.steps.findIndex(step => Comparator.isEqual(step.route, route))
            : -1;
    }

    public async authorizeAsync(): Promise<void> {
        this._key = null;
    }

    public async initializeAsync(): Promise<void> {
        if ((!this._initialized) && (!this._initializing)) {
            this._initializing = true;
            try {
                // Init All Dependencies
                EnumProvider.initialize();
                TransformProvider.initialize();
                Localizer.initialize();
                PageDefinitions.initialize();
                Comparator.initialize();

                //EnumProvider.initialize();
                //TransformProvider.initialize();
                //PageDefinitions.initialize();

                this._initialized = true;
            } finally {
                this._initializing = false;
            }
        }
    }

    public async completeActionAsync(): Promise<void> {
        //TODO: Process wizard actions

        this.context.wizard = new WizardContext();

        this.saveContext();
    }

    public async onLogoClickAsync(): Promise<void> {
        const route: PageRoute = (ch.isAuthenticated)
            ? (ch.getUser<User>().isAdmin)
                ? PageDefinitions.dashboardRoute
                : PageDefinitions.cruiseMyOrdersRoute
            : PageDefinitions.homeRoute;
        
        await PageRouteProvider.redirectAsync(route, false, true);
    }
    
    public async getAreasAsync(country?: string | null): Promise<CruiseArea[]> {
        return await ApiProvider.postAsync("api/cruise/getCountryAreasByCountryCode", country || "fi");
    }
    
    public async listCountriesAsync():Promise<CruiseCountry[]> {
        return await ApiProvider.postAsync("api/cruise/getActiveCountries");
    }
    
    public saveContext(): void {
        const json: string = JSON.stringify(this.context);
        window.localStorage.setItem(this.key, json);
    }

    public get context(): Context {
        if (this._context === null) {
            const json: string | null = window.localStorage.getItem(this.key);
            this._context = (json)
                ? Context.parse(json!)
                : new Context();
        }

        return this._context!;
    }

    public get userContext(): UserContext {
        return (ch.getContext() as UserContext);
    }

    public get wizard(): WizardContext {
        return this.context.wizard;
    }

    public get user(): User {
        return ch.getUser();
    }
    
    public clearCruiseWizard(): void {
        this.context.wizard = new WizardContext();
        
        const token: CrossAuthSyncUserTokenData | null = this.getCrossAuthSyncUserTokenData();
        if (token) {
            this.wizard.email = token.Email;
            this.wizard.phone = token.Phone;
            this.wizard.name = `${token.Firstname || ""} ${token.LastName || ""}`.trim();
            this.wizard.agreementAccepted = true;
            this.wizard.registrationAccepted = true;
            this.wizard.cancellationPolicyAccepted = true;
        }

        this.saveContext();
    }
    
    public get footerLinks(): IFooterLink[] {
        return [
            { href: CruiseConstants.companyFrontPage, label: Localizer.componentFooterFrontpageLanguageItemName },
            { href: "https://bout.fi/#contact", label: Localizer.componentFooterContactLanguageItemName },
        ];
    }
    
    public getCrossAuthSyncUserTokenData(): CrossAuthSyncUserTokenData | null {
        const context: ApplicationContext = ch.getContext();
        const tokenData: CrossAuthSyncUserTokenData | null = (context as any).tokenData as (CrossAuthSyncUserTokenData | null);
        return (tokenData?.IsCrossAuthSyncUserTokenData)
            ? tokenData
            : null;
    }
    
    public getReturnUrl(): string | null {
        const context: ApplicationContext = ch.getContext();
        const returnUrl = (context as any).returnUrl as (string | null);
        return (!!returnUrl)
            ? returnUrl
            : null;
    }

    public getFullPageTitle(title: string): string {
        const context: ApplicationContext = ch.getContext();
        return (!!title)
            ? `${title} - ${context.applicationName}`
            : context.applicationName;
    }
    
    public close(): void {
        const returnUrl: string | null = this.getReturnUrl();
        if (returnUrl) {
            ch.postToken(returnUrl);
        } else {
            window.location.href = CruiseConstants.companyFrontPage;
        }
    }
    
    public hasReturnUrl(): boolean {
        const returnUrl: string | null = this.getReturnUrl();
        return (!!returnUrl);
    }
    
    public async closeAsync(): Promise<void> {
        await ApiProvider.invokeWithForcedSpinnerAsync(async () => this.close(), true);
    }

    public getSteps(): IWizardSteps | null {
        const countryStep: IWizardStep = {route: PageDefinitions.cruiseWizardCountryRoute, title: Localizer.cruiseWizardCountryTitle};
        const startStep: IWizardStep = {route: PageDefinitions.cruiseWizardStartRoute, title: Localizer.cruiseWizardStartTitle};
        const callStep: IWizardStep = {route: PageDefinitions.cruiseWizardCallRoute, title: Localizer.cruiseWizardCallTitle};
        const dateStep: IWizardStep = {route: PageDefinitions.cruiseWizardDateRoute, title: Localizer.cruiseWizardDateTitle};
        const areaStep: IWizardStep = {route: PageDefinitions.cruiseWizardAreaRoute, title: Localizer.cruiseWizardAreaTitle};
        const passengersInfoStep: IWizardStep = {route: PageDefinitions.cruiseWizardPassengersInfoRoute, title: Localizer.cruiseWizardPassengersInfoTitle};
        const groupStep: IWizardStep = {route: PageDefinitions.cruiseWizardGroupRoute, title: Localizer.cruiseWizardGroupTitle};
        const extraStep: IWizardStep = {route: PageDefinitions.cruiseWizardExtraRoute, title: Localizer.cruiseWizardExtraTitle};
        const commentsStep: IWizardStep = {route: PageDefinitions.cruiseWizardCommentsRoute, title: Localizer.cruiseWizardCommentsTitle};
        const contactsStep: IWizardStep = {route: PageDefinitions.cruiseWizardContactsRoute, title: Localizer.cruiseWizardContactsTitle};
        const summaryStep: IWizardStep = {route: PageDefinitions.cruiseWizardSummaryRoute, title: Localizer.cruiseWizardSummaryTitle};

        //const context: Context = this.context;
        const wizard: WizardContext = this.wizard;
        const call: boolean = wizard.call || PageRoute.isEqual(PageDefinitions.cruiseWizardCallRoute, ch.getPage().route);
        
        if (call) {
            return {steps: [countryStep, startStep, callStep]};
        }
        
        const showExtras: boolean = (this.wizard.groupSize == CruiseGroupSize.Waterbus) || (this.wizard.groupSize == CruiseGroupSize.Fishing);
        
        if (showExtras) {
            return {steps: [countryStep, startStep, dateStep, areaStep, passengersInfoStep, groupStep, extraStep, commentsStep, contactsStep, summaryStep]};
        }

        return {steps: [countryStep, startStep, dateStep, areaStep, passengersInfoStep, groupStep, commentsStep, contactsStep, summaryStep]};
    }
    
    public async getPrevStepAsync(route: PageRoute): Promise<PageRoute> {
        const index: number = this.indexOfCurrentStep(route);
        const steps: IWizardStep[] = this.getSteps()!.steps;
        if ((index === -1) || (index === 0)) {
            return this.wizard.actionInitialPageRoute || PageDefinitions.cruiseWizardCountryRoute;
        }
        return steps[index - 1].route;
    }

    public async getNextStepAsync(route: PageRoute): Promise<IWizardNextStep> {
        const index: number = this.indexOfCurrentStep(route);
        const steps: IWizardStep[] = this.getSteps()!.steps;
        const firstStep: boolean = (index === -1) && (steps.length > 0);
        if (firstStep) {
            return {nextRoute: steps[0].route, firstStep: true, lastStep: false};
        }
        const lastStep: boolean = (index === -1) || (index === steps.length - 1);
        if (lastStep) {
            const wizard: WizardContext = this.wizard;
            const nextRoute: PageRoute | null = (!Comparator.isEqualPageRoute(wizard.actionInitialPageRoute, route))
                ? wizard.actionInitialPageRoute
                : null;
            return {nextRoute: nextRoute || PageDefinitions.cruiseWizardCountryRoute, firstStep: false, lastStep: true};
        }
        return {nextRoute: steps[index + 1].route, firstStep: false, lastStep: false};
    }
}

//Singleton
export default new CruiseController();